import { PageWrapper } from 'components';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetPwcCustomerQuery } from 'services/pwcCustomers';

import LoadingIndicator from 'components/loadingIndicator';

import RoutePath from 'router/path';

import { decoratePagination, getDefaultPagination, undecoratePagination } from 'utils/pagination';
import { usePersistentPagination } from 'utils/tables/pagination';
import { getRequestSorting, useTableSorting } from 'utils/tables/sorting';

import { useRedirectOnAccountSwitch } from '../../utils/useRedirectOnAccountSwitch';
import { usePwcCustomersAccountUuid } from '../usePwcCustomersAccountUuid';
import { CustomerDetails } from './customerDetails';
import { KEYS, LABELS } from './keys';

export const PwcCustomerViewContainer = () => {
  const { t } = useTranslation();
  useRedirectOnAccountSwitch(RoutePath.Pwc.Customers.Root);

  const params = useParams();
  const { uuid } = params;
  const accountUuid = usePwcCustomersAccountUuid();

  const { sorting, onSort } = useTableSorting({
    defaultSorting: {
      direction: KEYS.DEFAULT_TABLE_SORTING.DIRECTION,
      field: KEYS.DEFAULT_TABLE_SORTING.FIELD,
    },
  });

  const { pagination, setPagination, changeRowsPerPage, changePage } = usePersistentPagination(getDefaultPagination(), [
    sorting,
    accountUuid,
  ]);
  const { data: customerPwc, isLoading } = useGetPwcCustomerQuery(
    {
      accountUuid,
      customerUuid: uuid!,
      sort: getRequestSorting(sorting),
      pagination: undecoratePagination(pagination),
    },
    {
      skip: !uuid || !accountUuid,
    },
  );

  useEffect(() => {
    if (customerPwc?.data.orders.pagination) {
      setPagination(decoratePagination(customerPwc?.data.orders.pagination));
    }
  }, [customerPwc?.data.orders.pagination]);

  const breadcrumbs = LABELS.BREADCRUMBS.map((item) => t(item));

  if (!uuid || !accountUuid || isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  if (!customerPwc?.data) {
    return null;
  }

  return (
    <PageWrapper breadcrumbs={breadcrumbs} pageTitle={t(LABELS.PAGE_TITLE)}>
      <CustomerDetails
        pagination={pagination}
        onChangePage={changePage}
        onChangeRowsPerPage={changeRowsPerPage}
        customerPwcData={customerPwc?.data}
        isLoading={isLoading}
        onSort={onSort}
        sorting={sorting}
      />
    </PageWrapper>
  );
};
